import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { 
  X, 
  CheckSquare, 
  Square, 
  Users, 
  Lock, 
  Hash,
  AlertCircle,
  Download
} from 'lucide-react';

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  member_count: number;
  purpose?: string;
  topic?: string;
}

interface ChannelSelectionModalProps {
  channels: SlackChannel[];
  onClose: () => void;
  onImport: (selectedChannelIds: string[]) => Promise<void>;
  isLoading: boolean;
}

const ChannelSelectionModal: React.FC<ChannelSelectionModalProps> = ({
  channels,
  onClose,
  onImport,
  isLoading
}) => {
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  const toggleChannel = (channelId: string) => {
    const newSelected = new Set(selectedChannels);
    if (newSelected.has(channelId)) {
      newSelected.delete(channelId);
    } else {
      newSelected.add(channelId);
    }
    setSelectedChannels(newSelected);
  };

  const selectAll = () => {
    setSelectedChannels(new Set(channels.map(c => c.id)));
  };

  const selectNone = () => {
    setSelectedChannels(new Set());
  };

  const handleImport = async () => {
    if (selectedChannels.size === 0) return;
    
    try {
      setImporting(true);
      await onImport(Array.from(selectedChannels));
      onClose();
    } catch (error) {
      console.error('Failed to import channels:', error);
    } finally {
      setImporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">Loading channels...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden">
        <CardHeader className="border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Select Channels to Import</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Choose which Slack channels you want to import data from
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAll}
                disabled={importing}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={selectNone}
                disabled={importing}
              >
                Select None
              </Button>
            </div>
            <div className="text-sm text-gray-600">
              {selectedChannels.size} of {channels.length} channels selected
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="max-h-96 overflow-y-auto">
            {channels.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No channels available</h3>
                <p className="text-gray-500">
                  No Slack channels were found for import.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {channels.map((channel) => (
                  <div
                    key={channel.id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => toggleChannel(channel.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        {/* Selection Checkbox */}
                        <div className="flex-shrink-0">
                          {selectedChannels.has(channel.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </div>

                        {/* Channel Icon */}
                        <div className="flex-shrink-0">
                          {channel.is_private ? (
                            <Lock className="w-4 h-4 text-gray-500" />
                          ) : (
                            <Hash className="w-4 h-4 text-gray-500" />
                          )}
                        </div>

                        {/* Channel Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {channel.name}
                            </h4>
                            {channel.is_private && (
                              <Badge variant="secondary" className="text-xs">
                                Private
                              </Badge>
                            )}
                          </div>
                          
                          {(channel.purpose || channel.topic) && (
                            <p className="text-xs text-gray-600 mt-1 truncate">
                              {channel.purpose || channel.topic}
                            </p>
                          )}
                        </div>

                        {/* Member Count */}
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Users className="w-3 h-3" />
                          <span>{channel.member_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedChannels.size > 0 && (
                <>
                  Import data from {selectedChannels.size} selected channel{selectedChannels.size !== 1 ? 's' : ''}
                </>
              )}
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={selectedChannels.size === 0 || importing}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                {importing ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>Importing...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Import Selected</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChannelSelectionModal;







